import axios from "axios";
import { get } from "lodash";
import { toastAndRedirect } from "@/helpers/toastAndRedirect";
import { ApiResponse } from "@/types";

export interface Challenge {
    id: string;
    challenger_score_id: string;
    property: {
        id: string;
    };
}

export interface CreateChallengeParams {
    challenger_score_id: string;
}

export interface UnlockPropertyParams {
    winnerId: string;
    opponentId: string;
    propertyId: string;
    propertyAddress: string;
    winnerScore: number;
    opponentScore: number;
    challengeId: string;
    isTied?: boolean;
}

/**
 * Creates a challenge.
 * @param params - The parameters for creating the challenge.
 * @returns A promise that resolves with the created challenge data.
 */
export const createChallenge = async ({
    challenger_score_id,
}: CreateChallengeParams): Promise<ApiResponse<Challenge>> => {
    return await axios.post(`/api/challenges`, {
        challenger_score_id,
    });
};

/**
 * Retrieves property data for a given challenge.
 * @param challenge_id - The ID of the challenge.
 * @param userId - The ID of the user.
 * @param isAuthenticated - Whether the user is authenticated.
 * @returns A promise that resolves with the challenge's property data.
 */
export const getChallengePropertyData = async (
    challenge_id: string,
    userId: string,
    isAuthenticated: boolean,
): Promise<ApiResponse<Challenge> | null> => {
    let result = await axios.get(`/api/challenges/${challenge_id}/property`);
    let challengeData = get(result, "data", null);

    if (!challengeData) {
        // if no challenge data is returned, the challenge id has expired or do not exist
        toastAndRedirect("error", "CHALLENGE_EXPIRED", 2000, 3000);
    } else {
        const propertyId = get(challengeData, "property.id", null);
        if (challenge_id && propertyId && userId && isAuthenticated) {
            let guess = await axios.get(`/api/users/${userId}/guess`, {
                params: {
                    property_id: propertyId,
                },
            });
            const guessData = get(guess, "data.guess", null);
            if (guessData) {
                toastAndRedirect("error", "CHALLENGE_ACCEPTED", 2000, 3000);
                challengeData = null;
            }
        }
    }
    return result;
};

/**
 * Unlocks a property for the user once they have won a challenge.
 *
 * @param params - Parameters for unlocking the property
 * @returns A promise that resolves to the response from the API.
 * @throws Throws an error if the request fails.
 */
export async function unlockProperty({
    winnerId,
    opponentId,
    propertyId,
    propertyAddress,
    winnerScore,
    opponentScore,
    challengeId,
    isTied = false,
}: UnlockPropertyParams): Promise<ApiResponse<void>> {
    try {
        const response = await axios.post("/api/challenge/unlock_property", {
            winner_id: winnerId,
            opponent_id: opponentId,
            property_id: propertyId,
            property_address: propertyAddress,
            winner_score: winnerScore,
            opponent_score: opponentScore,
            challenge_id: challengeId,
            isTied,
        });
        return response.data;
    } catch (error) {
        throw new Error("Failed to unlock property: " + (error as Error).message);
    }
}
